import React, { useState } from "react";
import { useQuote } from "../context/QuoteContext";
import api from "../api";
import { uid, calcVolume, ceil10 } from "../utils";
import { Button } from "./ui/button";
import { Camera, Loader2, X, MousePointerClick, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const photoSrc = (p) => p.dataUrl || (/^(data:|https?:)/.test(p.url) ? p.url : `${BACKEND_URL}${p.url}`);
const CONF_DOT = { high: "#22c55e", medium: "#f59e0b", low: "#ef4444" };

export default function PhotoInventory() {
  const { imagePhotos, items, addItemsBulk, removeItem, settings } = useQuote();
  const pricePerM3 = settings?.pricePerM3 ?? 62.5;
  const [adding, setAdding] = useState(null);
  const [addMode, setAddMode] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const toggleCollapse = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  if (!imagePhotos || imagePhotos.length === 0) return null;

  const marked = items.filter((it) => it.photoId && it.marker);
  const numById = {};
  marked.forEach((it, i) => { numById[it.id] = i + 1; });

  const onPhotoClick = async (photo, e) => {
    if (!addMode || adding) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    setAdding(photo.id);
    try {
      const imageUrl = photo.dataUrl || photo.url;
      const { data } = await api.post("/ai/analyze-point", { imageUrl, x, y });
      const l = Math.round(Number(data.length_cm) > 0 ? data.length_cm : 40);
      const w = Math.round(Number(data.width_cm) > 0 ? data.width_cm : 40);
      const h = Math.round(Number(data.height_cm) > 0 ? data.height_cm : 40);
      addItemsBulk([{
        id: uid(), tipo: data.label || "Oggetto", qty: 1, dims: { l, w, h },
        pricePerMonth: ceil10(calcVolume(l, w, h) * pricePerM3),
        note: "", fachMinutes: 0, smontMinutes: 0, imbMinutes: 0,
        includeSmont: !!data.is_disassemblable, includeImb: false,
        isPriceOverridden: true, isDimOverridden: true, fromCatalog: false,
        photoId: photo.id, marker: { x, y }, confidence: data.confidence || "medium",
      }]);
      toast.success(`Aggiunto: ${data.label || "oggetto"}`);
      setAddMode(false); // torna alla vista normale dopo un click
    } catch {
      toast.error("Impossibile riconoscere l'oggetto");
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="photo-inventory">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Camera className="h-5 w-5 text-[#1a5f5a]" />
          <h3 className="font-display font-semibold text-lg">Foto analizzate</h3>
        </div>
        <Button size="sm" variant={addMode ? "default" : "outline"} onClick={() => setAddMode((m) => !m)}
          className={addMode ? "bg-[#1a5f5a] hover:bg-[#134744] gap-1.5" : "gap-1.5"} data-testid="photo-add-mode-toggle">
          <MousePointerClick className="h-4 w-4" /> {addMode ? "Modalità aggiunta attiva" : "Aggiungi oggetto dalla foto"}
        </Button>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        {addMode
          ? "Clicca sul punto dell'oggetto nella foto: l'AI proverà a riconoscerlo e ad aggiungerlo con dimensioni stimate."
          : "I numeri indicano gli oggetti del preventivo. Usa la × su un marker per rimuoverlo. Attiva \"Aggiungi oggetto dalla foto\" per riconoscere un altro oggetto cliccando sulla foto."}
      </p>
      <div className={`grid gap-4 ${addMode ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
        {imagePhotos.map((p) => {
          const photoItems = marked.filter((it) => it.photoId === p.id);
          const isCollapsed = !!collapsed[p.id];
          return (
            <div key={p.id} className="flex flex-col gap-2">
              <div
                className={`relative rounded-md overflow-hidden border bg-slate-100 ${addMode ? "border-[#1a5f5a] cursor-crosshair ring-2 ring-[#1a5f5a]/40" : "border-slate-200"}`}
                data-testid={`inv-photo-${p.id}`} onClick={(e) => onPhotoClick(p, e)}>
                <img src={photoSrc(p)} alt="Foto" className="w-full h-auto block bg-slate-50" draggable={false} />
                {adding === p.id && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-white" />
                  </div>
                )}
                {photoItems.map((it) => (
                  <div key={it.id} className="absolute -translate-x-1/2 -translate-y-1/2"
                    style={{ left: `${it.marker.x * 100}%`, top: `${it.marker.y * 100}%` }}>
                    <div className="relative">
                      <span className="rounded-full text-white text-xs font-bold flex items-center justify-center border-2 border-white shadow"
                        title={it.tipo} data-testid={`inv-marker-${numById[it.id]}`}
                        style={{ width: 26, height: 26, background: CONF_DOT[it.confidence] || CONF_DOT.medium }}>
                        {numById[it.id]}
                      </span>
                      <button onClick={(e) => { e.stopPropagation(); removeItem(it.id); }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 shadow"
                        title="Rimuovi oggetto" data-testid={`inv-marker-remove-${numById[it.id]}`}>
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {photoItems.length > 0 && (
                <div className="border border-slate-200 rounded-md bg-white" data-testid={`inv-list-${p.id}`}>
                  <button type="button" onClick={() => toggleCollapse(p.id)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 rounded-md"
                    data-testid={`inv-list-toggle-${p.id}`}>
                    <span className="font-medium text-slate-700">Oggetti rilevati in questa foto ({photoItems.length})</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isCollapsed ? "" : "rotate-180"}`} />
                  </button>
                  {!isCollapsed && (
                    <div className="px-3 pb-2">
                      {photoItems.map((it) => (
                        <div key={it.id} className="flex items-center gap-2 text-xs py-1.5 border-t border-slate-100" data-testid={`inv-list-item-${numById[it.id]}`}>
                          <span className="w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center shrink-0"
                            style={{ background: CONF_DOT[it.confidence] || CONF_DOT.medium }}>{numById[it.id]}</span>
                          <span className="flex-1 truncate text-slate-700">{it.tipo}{it.qty > 1 ? ` ×${it.qty}` : ""}</span>
                          <span className="text-slate-400 tabular-nums hidden sm:inline">{it.dims.l}×{it.dims.w}×{it.dims.h} cm</span>
                          <span className="text-slate-600 tabular-nums">€{(ceil10((it.pricePerMonth || 0) * it.qty)).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}/mese</span>
                          <button onClick={() => removeItem(it.id)} title="Rimuovi" data-testid={`inv-list-remove-${numById[it.id]}`}
                            className="text-slate-300 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
